import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { AuthData, withAuthContext } from '../context/auth';
import { Policy2 } from '../types/policy';
import { PremiumReportResponse } from '../types/premiumReports';
import { tableStyles } from '../util/styles';
import { getComparator, HeadCell, Order, stableSort } from '../util/tableUtil';
import { capitalize } from '../util/util';
import { StyledEmptyMessage } from './common/index';
import { getPremiumReportStartDate } from './premiumReports/util';
import SortableTable from './SortableTable';

interface Props {
	premiumReports: PremiumReportResponse[];
	policy: Policy2;
	authData: AuthData;
}

interface Data {
	premiumReportId: string;
	startDate: string;
	endDate: string;
	dateCreated: string;
	lastUpdated: string;
	invoiceDueDate: string;
	status: string;
	link: string;
}

function createData(
	premiumReportId: string,
	startDate: string,
	endDate: string,
	dateCreated: string,
	lastUpdated: string,
	invoiceDueDate: string,
	status: string,
	link: string,
): Data {
	return {
		premiumReportId,
		startDate,
		endDate,
		dateCreated,
		lastUpdated,
		invoiceDueDate,
		status,
		link,
	};
}

const PremiumReportsTable = (props: Props) => {
	const { premiumReports, policy, authData } = props;
	const params = useParams();
	const { policyId } = params;
	const [order, setOrder] = React.useState<Order>('asc');
	const [orderBy, setOrderBy] = React.useState<string>('dateCreated');

	const emptyTable = !premiumReports.length ? (
		<StyledEmptyMessage>No Premium Reports</StyledEmptyMessage>
	) : null;
	const classes = tableStyles();

	const headCells: HeadCell[] = [
		{ id: 'premiumReportId', rightAlign: false, label: 'Premium Report Id' },
		{ id: 'startDate', rightAlign: false, label: 'Start Date' },
		{ id: 'endDate', rightAlign: false, label: 'End Date' },
		{ id: 'dateCreated', rightAlign: false, label: 'Date Created' },
		{ id: 'lastUpdated', rightAlign: false, label: 'Last Updated' },
		{ id: 'invoiceDueDate', rightAlign: false, label: 'Invoice Due Date' },
		{ id: 'status', rightAlign: false, label: 'Current Status' },
	];

	const rows: any = premiumReports.map((premiumReport: any, index: number) => {
		const startTimestamp = getPremiumReportStartDate(
			premiumReport,
			premiumReports,
			policy,
		);
		const premiumReportState = premiumReport.replacedByLocator
			? 'replaced'
			: premiumReport.state;
		return createData(
			premiumReport.locator,
			startTimestamp,
			premiumReport.endTimestamp,
			premiumReport.createdTimestamp,
			premiumReport.updatedTimestamp,
			premiumReport.invoiceDueTimestamp,
			capitalize(premiumReportState),
			`/policy/${policyId}/premiumReports/${premiumReport.locator}/details`,
		);
	});

	const rowElements = stableSort(rows, getComparator(order, orderBy)).map(
		(row: any, index: number) => {
			return (
				<TableRow
					component={Link}
					to={row.link}
					hover
					tabIndex={-1}
					key={row.premiumReportId}
					className={classes.tableRow}
				>
					<TableCell scope='row'>{row.premiumReportId}</TableCell>
					<TableCell>{authData.getFormattedDate(row.startDate)}</TableCell>
					<TableCell>{authData.getFormattedDate(row.endDate)}</TableCell>
					<TableCell>{authData.getFormattedDate(row.dateCreated)}</TableCell>
					<TableCell>{authData.getFormattedDate(row.lastUpdated)}</TableCell>
					<TableCell>{authData.getFormattedDate(row.invoiceDueDate)}</TableCell>
					<TableCell>{row.status}</TableCell>
				</TableRow>
			);
		},
	);

	return (
		<SortableTable
			rows={rowElements}
			headCells={headCells}
			additionalMsg={emptyTable}
			order={order}
			setOrder={setOrder}
			orderBy={orderBy}
			setOrderBy={setOrderBy}
		/>
	);
};

export default withAuthContext<Props>(PremiumReportsTable);
